import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { deviceRegion, formatPhoneForDisplay } from '@vav/protocol';
import {
  composeInternational,
  countryFor,
  countryNamesVersion,
  countryOptions,
  matchesCountry,
  subscribeCountryNames,
} from '@vav/i18n';

import { useI18n } from '../i18n.tsx';

interface Props {
  label: string;
  onChange: (composed: string) => void;
}

/**
 * Country selector plus a national number.
 *
 * Mirrors the mobile field deliberately. A single free-text box made the user
 * responsible for rules they have no reason to know — trunk zero or not,
 * country code or not — and gave no feedback about what was actually stored.
 * The browser usually supplies a full locale so detection works more often
 * here than on a phone, but "more often" is not "always", and the silence after
 * saving was the same on both.
 */
export function PhoneField({ label, onChange }: Props) {
  const { t, locale } = useI18n();
  const selectId = useId();
  const inputId = useId();

  // Browsers ship `Intl.DisplayNames`, so the names are usually there on the
  // first render — but the web client also runs where they are not, and this is
  // the same subscription the mobile field uses. Keeping both on it means the
  // list cannot silently go stale on one client only.
  const names = useSyncExternalStore(subscribeCountryNames, countryNamesVersion, countryNamesVersion);

  const options = useMemo(() => countryOptions(locale), [locale, names]);

  // Region code rather than the resolved option: holding the option pinned the
  // name it had at mount.
  const [region, setRegion] = useState<string | undefined>(
    () => (countryFor(locale, deviceRegion()) ?? countryFor(locale, 'US'))?.region,
  );
  const country = useMemo(() => countryFor(locale, region), [locale, region, names]);
  const [national, setNational] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(
    () => options.filter((option) => matchesCountry(option, filter)),
    [options, filter],
  );

  // Opening a search box and then making the user tap it is one tap too many.
  useEffect(() => {
    if (pickerOpen) searchRef.current?.focus();
    else setFilter('');
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setPickerOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerOpen]);

  /** What the server will actually store, or undefined while it is incomplete. */
  const resolved = country ? formatPhoneForDisplay(national, country.region) : undefined;

  function update(nextNational: string, nextRegion = region) {
    setNational(nextNational);
    setRegion(nextRegion);
    onChange(composeInternational(locale, nextNational, nextRegion));
  }

  return (
    <div className="phoneField">
      <label htmlFor={inputId}>{label}</label>

      <div className="phoneField__row">
        {/*
          A <select> holding ~250 countries is a wall to scroll through. Desktop
          browsers give it type-ahead, phones give it a wheel and nothing else,
          so the one place this list is hardest to use is the one where it had no
          search at all. The mobile client has had a searchable picker since the
          field was built; this is the same list, the same matcher, and the same
          catalogue key.
        */}
        <button
          type="button"
          id={selectId}
          className="phoneField__country"
          aria-label={t('phone.country')}
          aria-haspopup="listbox"
          aria-expanded={pickerOpen}
          onClick={() => setPickerOpen(true)}
        >
          <span>{country?.name ?? t('phone.country')}</span>
          {country && <span className="phoneField__code">+{country.callingCode}</span>}
        </button>

        <input
          id={inputId}
          value={national}
          onChange={(e) => update(e.target.value)}
          placeholder={t('phone.national')}
          inputMode="tel"
          autoComplete="tel-national"
          // A phone number is data: it reads left-to-right even in an RTL page.
          dir="ltr"
        />
      </div>

      {/* Silence here is the defect this component exists to remove. */}
      {resolved ? (
        <p className="phoneField__resolved">{t('phone.willSave', { number: resolved })}</p>
      ) : national.trim() ? (
        <p className="hint">{t('phone.invalidHint')}</p>
      ) : null}

      {pickerOpen && (
        <div className="countryPicker" role="dialog" aria-modal="true" onClick={() => setPickerOpen(false)}>
          <div className="countryPicker__panel" onClick={(e) => e.stopPropagation()}>
            <div className="countryPicker__head">
              <strong>{t('phone.country')}</strong>
              <button
                type="button"
                className="icon"
                onClick={() => setPickerOpen(false)}
                aria-label={t('thread.cancelReply')}
              >
                ✕
              </button>
            </div>

            <input
              ref={searchRef}
              className="countryPicker__search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('phone.searchCountry')}
              autoComplete="off"
              dir="auto"
            />

            <ul className="countryPicker__list" role="listbox">
              {filtered.map((option) => (
                <li key={option.region}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.region === country?.region}
                    onClick={() => {
                      update(national, option.region);
                      setPickerOpen(false);
                    }}
                  >
                    <span>{option.name}</span>
                    <span className="muted">+{option.callingCode}</span>
                    {option.region === country?.region && <span aria-hidden="true">✓</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
